import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Search,
  KeyRound,
  ScrollText,
  ShieldCheck,
  Settings,
  LogOut,
  Database,
  HardDrive,
  FolderKanban,
  TableProperties,
  FileSpreadsheet,
  Tags,
  ListChecks,
  FileText,
  ShoppingBag,
  BarChart3,
  Radar,
  FlaskConical,
  SlidersHorizontal,
  Image,
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
  ShieldBan,
  GitBranch,
  Compass,
  Terminal,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { APP_VERSION } from "@/lib/version";
import { alibabaApi, type InsightsSummaryItem } from "@/api/alibaba";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const workflowNav: NavItem[] = [
  { title: "專案管理", url: "/projects", icon: FolderKanban },
  { title: "關鍵字群組", url: "/keyword-groups", icon: Tags },
  { title: "關鍵字搜尋", url: "/keyword-search", icon: KeyRound },
  { title: "批量網址解析", url: "/batch-scraper", icon: FileText },
  { title: "阿里巴巴搜尋", url: "/alibaba-search", icon: ShoppingBag },
  // "品項洞察" is now a dynamic submenu rendered separately
  { title: "SERP 分析報告", url: "/analysis-reports", icon: FlaskConical },
  { title: "任務列表", url: "/tasks", icon: ListChecks },
];

const dataNav: NavItem[] = [
  { title: "資料瀏覽器", url: "/serp-analysis", icon: Database },
  { title: "資料檢視", url: "/data-viewer", icon: TableProperties },
  { title: "專案匯出", url: "/project-export", icon: FileSpreadsheet },
  { title: "資料與備份", url: "/data-storage", icon: HardDrive },
];

const systemNav: NavItem[] = [
  { title: "Pipeline 監控", url: "/pipeline-monitor", icon: GitBranch },
  { title: "Nav Health", url: "/nav-health", icon: Compass },
  { title: "圖片過濾", url: "/image-blocklist", icon: ShieldBan },
  { title: "爬取策略", url: "/crawl-strategies", icon: Radar },
  { title: "AI Prompt 管理", url: "/prompt-manager", icon: Terminal },
  { title: "系統日誌", url: "/logs", icon: ScrollText },
  { title: "管理員", url: "/admin", icon: ShieldCheck, adminOnly: true },
  { title: "SERP Settings", url: "/admin/serp-settings", icon: SlidersHorizontal, adminOnly: true },
  { title: "ShareMyImage", url: "/admin/sharemyimage", icon: Image, adminOnly: true },
];

function NavSection({ label, items, isAdmin }: { label: string; items: NavItem[]; isAdmin: boolean }) {
  const filtered = items.filter(item => !item.adminOnly || isAdmin);
  if (filtered.length === 0) return null;
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {filtered.map(item => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild>
                <NavLink
                  to={item.url}
                  end={item.url === "/"}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                  activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// ── Dynamic Insights Submenu ──

function InsightsSubMenu() {
  const [open, setOpen] = useState(false);

  const { data: summaryItems, isLoading } = useQuery({
    queryKey: ["sidebar-alibaba-keywords"],
    queryFn: async () => {
      const res = await alibabaApi.getInsightsSummary();
      return (res.data ?? []) as InsightsSummaryItem[];
    },
    staleTime: 60_000, // cache 1 min to avoid sidebar flicker
  });

  // Only show keywords that actually have Alibaba product data
  const activeTags = summaryItems ?? [];

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors cursor-pointer w-full"
      >
        <BarChart3 className="h-4 w-4" />
        <span className="flex-1 text-left">品項洞察</span>
        {isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </SidebarMenuButton>
      {open && (
        <SidebarMenu className="ml-4 mt-1">
          {/* Summary page link */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink
                to="/alibaba-insights"
                end
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors text-sm"
                activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                <span>品項總覽</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {activeTags.length === 0 && !isLoading && (
            <SidebarMenuItem>
              <span className="px-3 py-1.5 text-xs text-muted-foreground">
                尚無品項資料
              </span>
            </SidebarMenuItem>
          )}
          {activeTags.map(item => (
            <SidebarMenuItem key={item.tag}>
              <SidebarMenuButton asChild>
                <NavLink
                  to={`/alibaba-insights/${encodeURIComponent(item.tag)}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors text-sm"
                  activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                >
                  <Package className="h-3.5 w-3.5" />
                  <span className="truncate flex-1">{item.tag}</span>
                  <span className="text-[10px] text-muted-foreground bg-muted rounded-full px-1.5">
                    {item.total_products}
                  </span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      )}
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    signOut();
    window.location.href = "/login";
  };

  return (
    <Sidebar className="border-r border-sidebar-border">
      <div className="flex h-14 items-center gap-2 px-4 border-b border-sidebar-border">
        <Search className="h-5 w-5 text-sidebar-primary" />
        <span className="font-bold text-lg text-sidebar-primary-foreground">
          SEO 分析器
        </span>
      </div>

      <SidebarContent>
        {/* Dashboard standalone */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/"
                    end
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Dashboard</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Workflow section with dynamic Insights submenu */}
        <SidebarGroup>
          <SidebarGroupLabel>研究流程</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {workflowNav.filter(item => !item.adminOnly || isAdmin).map(item => {
                // Insert InsightsSubMenu after "阿里巴巴搜尋"
                const isAlibaba = item.url === "/alibaba-search";
                return (
                  <span key={item.title}>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          end={item.url === "/"}
                          className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {isAlibaba && <InsightsSubMenu />}
                  </span>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <NavSection label="資料工具" items={dataNav} isAdmin={isAdmin} />
        <NavSection label="系統" items={systemNav} isAdmin={isAdmin} />
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          {user && (
            <SidebarMenuItem>
              <div className="flex items-center gap-3 px-3 py-2 text-sm text-sidebar-foreground">
                {user.avatar_url && (
                  <img src={user.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                )}
                <span className="truncate">{user.display_name}</span>
              </div>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink
                to="/settings"
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                activeClassName="bg-sidebar-accent text-sidebar-primary"
              >
                <Settings className="h-4 w-4" />
                <span>設定</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors cursor-pointer">
              <LogOut className="h-4 w-4" />
              <span>登出</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="px-3 py-1 text-[11px] text-sidebar-foreground/40 select-none">
          v{APP_VERSION}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
